import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MovieCard from '../components/MovieCard';
import { moviesAPI } from '../api';
import { Film, Calendar, Clock } from 'lucide-react';

const GenreMovies = () => {
  const { genre } = useParams();
  const navigate = useNavigate();
  const [nowPlayingMovies, setNowPlayingMovies] = useState([]);
  const [upcomingMovies, setUpcomingMovies] = useState([]);
  const [archivedMovies, setArchivedMovies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Türkçe başlık için mapping
  const genreTitles = {
    'aksiyon': 'Aksiyon',
    'komedi': 'Komedi',
    'drama': 'Drama',
    'bilim-kurgu': 'Bilim Kurgu',
    'korku': 'Korku',
    'romantik': 'Romantik',
    'gerilim': 'Gerilim',
    'animasyon': 'Animasyon',
    'macera': 'Macera',
    'fantastik': 'Fantastik',
    'aile': 'Aile',
    'belgesel': 'Belgesel',
    'suç': 'Suç',
    'biyografi': 'Biyografi',
    'müzikal': 'Müzikal',
    'tarihi': 'Tarihi',
    'western': 'Western',
    'savaş': 'Savaş',
  };

  const genreTitle = genreTitles[genre] || genre.charAt(0).toUpperCase() + genre.slice(1);

  useEffect(() => {
    loadMoviesByGenre();
  }, [genre]);

  const loadMoviesByGenre = async () => {
    try {
      setLoading(true);
      const response = await moviesAPI.getAll();
      const allMovies = response.data;

      // Türe göre filtrele (case-insensitive)
      const filteredMovies = allMovies.filter(movie => {
        if (!movie.genres || movie.genres.length === 0) return false;
        return movie.genres.some(g => 
          g.toLowerCase().replace(/\s+/g, '-') === genre.toLowerCase()
        );
      });

      // Status'e göre ayır
      const nowPlaying = filteredMovies.filter(m => m.status === 'now_playing');
      const upcoming = filteredMovies.filter(m => m.status === 'upcoming');
      const archived = filteredMovies.filter(m => m.status === 'archived');

      setNowPlayingMovies(nowPlaying);
      setUpcomingMovies(upcoming);
      setArchivedMovies(archived);

      // Eğer hiç film yoksa 404'e yönlendir
      if (filteredMovies.length === 0) {
        setTimeout(() => navigate('/'), 3000);
      }
    } catch (error) {
      console.error('Error loading genre movies:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <Film className="w-16 h-16 text-[#FDB714] animate-pulse mx-auto mb-4" />
          <p className="text-gray-400">Filmler yükleniyor...</p>
        </div>
      </div>
    );
  }

  const totalMovies = nowPlayingMovies.length + upcomingMovies.length + archivedMovies.length;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Helmet>
        <title>{genreTitle} Filmleri - Cinefora Sinemaları</title>
        <meta name="description" content={`${genreTitle} kategorisindeki tüm filmler. Vizyondaki ve yakında vizyona girecek ${genreTitle.toLowerCase()} filmleri.`} />
      </Helmet>
      
      <Header />
      
      <main className="container mx-auto px-4 py-8 md:py-12">
        {/* Hero Section */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full px-6 py-3 mb-6">
            <Film className="w-6 h-6 text-[#FDB714]" />
            <span className="text-[#FDB714] font-semibold">Tür</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
            {genreTitle} Filmleri
          </h1>
          
          <p className="text-gray-400 text-lg">
            {totalMovies} film bulundu
          </p>
        </div>

        {totalMovies === 0 ? (
          <div className="text-center py-20">
            <Film className="w-20 h-20 text-gray-700 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-300 mb-4">
              Bu türde film bulunamadı
            </h2>
            <p className="text-gray-500 mb-8">
              Şu anda {genreTitle} kategorisinde gösterimde olan film bulunmuyor.
            </p>
            <button
              onClick={() => navigate('/vizyondaki-filmler')}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold px-8 py-3 rounded-lg transition-all duration-300"
            >
              Tüm Filmlere Göz At
            </button>
          </div>
        ) : (
          <div className="space-y-16">
            {/* Now Playing */}
            {nowPlayingMovies.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-8">
                  <Calendar className="w-6 h-6 text-green-500" />
                  <h2 className="text-3xl font-bold text-white">
                    Vizyondaki {genreTitle} Filmleri
                  </h2>
                  <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-semibold">
                    {nowPlayingMovies.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                  {nowPlayingMovies.map(movie => (
                    <MovieCard key={movie._id} movie={movie} />
                  ))}
                </div>
              </section>
            )}

            {/* Upcoming */}
            {upcomingMovies.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-8">
                  <Clock className="w-6 h-6 text-blue-500" />
                  <h2 className="text-3xl font-bold text-white">
                    Yakında Vizyonda
                  </h2>
                  <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm font-semibold">
                    {upcomingMovies.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                  {upcomingMovies.map(movie => (
                    <MovieCard key={movie._id} movie={movie} />
                  ))}
                </div>
              </section>
            )}

            {/* Archived */}
            {archivedMovies.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-8">
                  <Film className="w-6 h-6 text-gray-500" />
                  <h2 className="text-3xl font-bold text-white">
                    Vizyondan Kalkmış Filmler
                  </h2>
                  <span className="bg-gray-500/20 text-gray-400 px-3 py-1 rounded-full text-sm font-semibold">
                    {archivedMovies.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                  {archivedMovies.map(movie => (
                    <MovieCard key={movie._id} movie={movie} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default GenreMovies;
